import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import FooterShareButton from './FooterShareButton'
import { Button } from '@/components/ui/button'
import { INSTAGRAM_URL } from '../constants/external-links'

const quickLinkIconClass =
  'shrink-0 w-5 h-5 mr-3 text-white transition-colors duration-200 group-hover:text-primary-golden'

const QUICK_LINK_ICONS = {
  about:
    'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  careers:
    'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  resources:
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  contact:
    'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  sitemap:
    'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  hub:
    'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
}

function QuickLinkIcon ({ iconKey }) {
  const d = QUICK_LINK_ICONS[iconKey]
  if (!d) return null
  return (
    <svg className={quickLinkIconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
  )
}

function FooterSocials () {
  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <a
        href="https://www.facebook.com/profile.php?id=61578353949539"
        target="_blank"
        rel="noopener noreferrer"
        className="group flex h-11 w-11 items-center justify-center rounded-lg bg-blue-600 transition-all duration-300 hover:scale-105 hover:bg-blue-700"
        aria-label="Facebook"
      >
        <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      </a>
      <a
        href={INSTAGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex h-11 w-11 items-center justify-center rounded-lg bg-linear-to-r from-purple-500 to-pink-500 transition-all duration-300 hover:scale-105 hover:from-purple-600 hover:to-pink-600"
        aria-label="Instagram"
      >
        <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      </a>
      <a
        href="https://www.psychologytoday.com/us/therapists/lidiana-cartolano-boca-raton-fl/1038382"
        target="_blank"
        rel="noopener noreferrer"
        className="group flex h-11 min-w-[4.5rem] items-center justify-center rounded-lg px-2 transition-all duration-300 hover:scale-105"
        aria-label="Psychology Today Profile"
      >
        <img
          src="/img/Psychology-Today-Verified.png"
          alt="Psychology Today Verified"
          className="h-9 w-auto object-contain"
        />
      </a>
      <a
        href="https://care.headway.co/providers/lidiana-cartolano"
        target="_blank"
        rel="noopener noreferrer"
        className="group flex h-11 min-w-[4.5rem] items-center justify-center rounded-lg bg-teal-700 px-2 text-xs font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-teal-800"
        aria-label="Headway profile"
      >
        Headway
      </a>
    </div>
  )
}

const Footer = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const language = useCurrentLanguage()

  const langPath = (path) => {
    const p = path.startsWith('/') ? path : `/${path}`
    if (language === 'pt') return `/pt${p}`
    if (language === 'en') return `/en${p}`
    return p
  }

  const quickLinks = [
    { id: 'about', to: langPath('/about'), label: 'About', icon: 'about' },
    { id: 'locations', to: langPath('/locations'), labelKey: 'common.nav.locations', icon: 'about' },
    { id: 'careers', to: langPath('/careers'), labelKey: 'common.nav.careers', icon: 'careers' },
    { id: 'resources', to: langPath('/resources'), labelKey: 'common.nav.resources', icon: 'resources' },
    { id: 'contact', to: langPath('/contact'), labelKey: 'common.nav.contact', icon: 'contact' },
    { id: 'sitemap', to: langPath('/sitemap'), label: 'Sitemap', icon: 'sitemap' },
    { id: 'hub', to: '/hub', label: 'VIVA HUB', icon: 'hub' },
  ]

  // Determine version to display (show dev version on localhost or in dev mode)
  const baseVersion = import.meta.env.VITE_APP_VERSION || '1.0.0'
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development'
  const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
     window.location.hostname === '127.0.0.1' ||
     window.location.hostname === '[::1]')
  const displayVersion = (isDev || isLocalhost) ? '0.0.0-dev' : baseVersion

  if (isLoading) {
    return (
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-pulse bg-gray-700 h-4 w-48 mx-auto rounded mb-4"></div>
            <div className="animate-pulse bg-gray-700 h-3 w-32 mx-auto rounded"></div>
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 pb-[max(4rem,calc(env(safe-area-inset-bottom,0px)+3rem))] pt-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Brand + socials */}
          <div className="lg:col-span-4">
            <div className="flex flex-col items-center text-center">
              <img
                src="/img/logo/Color-logo-no-background-300x300.png"
                alt="VIVA Psychotherapy Logo"
                className="mb-6 h-28 w-auto object-contain sm:h-32 md:h-36"
              />
              <h2 className="mb-3 text-3xl font-bold text-white">{t('common.brand.name')}</h2>
              <p className="mb-6 text-lg font-medium text-gray-300">
                {t('common.brand.tagline')}
              </p>
              <p className="mx-auto mb-8 max-w-prose text-pretty text-center text-base leading-relaxed text-gray-400">
                {t('common.brand.description')}
              </p>

              <div className="w-full max-w-sm border-t border-gray-700/80 pt-8">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {t('common.footer.socials')}
                </h3>
                <FooterSocials />
              </div>
            </div>
          </div>

          {/* Contact + quick links */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 gap-8">
              <div className="bg-gray-800/50 rounded-lg p-6">
                <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                  <h3 className="text-xl font-semibold text-white">{t('common.footer.contactUs')}</h3>
                  <FooterShareButton />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-center group">
                      <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mr-4 transition-colors duration-200">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">{t('common.contact.phoneLabel')}</p>
                        <a
                          href={`tel:${t('common.contact.phone').replace(/\D/g, '')}`}
                          className="text-white font-medium hover:text-primary-golden transition-colors duration-200"
                        >
                          {t('common.contact.phone')}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center group">
                      <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mr-4 transition-colors duration-200">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">{t('common.contact.emailLabel')}</p>
                        <a
                          href={`mailto:${t('common.contact.email')}`}
                          className="text-white font-medium hover:text-primary-golden transition-colors duration-200"
                        >
                          {t('common.contact.email')}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start group">
                      <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mr-4 mt-0.5 transition-colors duration-200">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">{t('common.contact.addressLabel')}</p>
                        <p className="text-white font-medium">{t('common.contact.address')}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3">{t('common.footer.ourLocation')}</h4>
                    <div className="group relative">
                      <iframe
                        src={`https://www.google.com/maps?q=${encodeURIComponent('5301 N Federal Hwy #200, Boca Raton, FL 33487')}&output=embed&hl=en&z=15`}
                        width="100%"
                        height="200"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="rounded-lg shadow-lg min-h-[200px]"
                        title="VIVA Psychotherapy Location"
                      ></iframe>
                    </div>
                    <Button asChild variant="brand" size="xl" className="mt-4 w-full touch-manipulation">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t('common.contact.address'))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('common.footer.openInMaps')}
                      </a>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-6 text-white">
                  {t('common.footer.quickLinks')}
                </h3>
                <nav
                  className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1"
                  aria-label={t('common.footer.quickLinks')}
                >
                  {quickLinks.map((item) => (
                    <Link
                      key={item.id}
                      to={item.to}
                      className="flex min-h-11 touch-manipulation items-center rounded-lg py-2 text-gray-300 transition-colors duration-200 hover:bg-white/5 hover:text-white group"
                    >
                      <QuickLinkIcon iconKey={item.icon} />
                      <span>{item.labelKey ? t(item.labelKey) : item.label}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <p className="text-gray-400">
            {t('common.footer.copyright')}
          </p>
          <p className="text-gray-500 text-sm mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <Link
              to={language === 'pt' ? '/pt/sms-privacy-policy' : '/sms-privacy-policy'}
              className="hover:text-primary-golden transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-600" aria-hidden>
              ·
            </span>
            <Link
              to={language === 'pt' ? '/pt/sms-terms' : '/sms-terms'}
              className="hover:text-primary-golden transition-colors"
            >
              Terms
            </Link>
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Version {displayVersion}
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
